import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  IconButton,
  StatusIndicator,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  LocationAwayIcon,
  BedIcon,
  AccessibleIcon,
  ListIcon,
  MoneyAltIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
  PencilIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useWaitlistCard } from './hooks';
import messages from './messages';
import { formatDate, formatMoney } from '../../../utils/formatter';
import {
  green,
  warning,
} from '@fortress-technology-solutions/fortress-component-library/design';
import DenyFromWaitlist from '../../../components/AffordableWaitlistActions/DenyFromWaitlist';
import CancelFromWaitlist from '../../../components/AffordableWaitlistActions/CancelFromWaitlist';
import SelectFromWaitlist from '../../../components/AffordableWaitlistActions/SelectFromWaitlist';
import ReinstateFromWaitlist from '../../../components/AffordableWaitlistActions/ReinstateFromWaitlist';
import EditWaitlist from '../../../components/AffordableWaitlistActions/EditWaitlist';
import AddToWaitlist from '../../../components/AffordableWaitlistActions/AddToWaitlist';
import ConditionalTooltip from '../../../components/ConditionalTooltip';

// The qualifying bands, darkening as the household nears the top of the scale. Over Income and
// Not Determined are deliberately absent: they get no dot.
const INCOME_LEVEL_DOT_COLOR = {
  'Extremely Low': green.dark,
  'Very Low': green.light,
  Low: warning.main,
  Moderate: warning.dark,
};

const DetailRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" width="100%">
    <Typography variant="bodyMedium">{label}</Typography>
    <Typography variant="bodyMedium">{value}</Typography>
  </Stack>
);

const PositionStatus = ({ message, status }) =>
  message ? <FormattedMessage {...message} /> : status ?? '---';

const SectionTitle = ({ icon, children, action }) => (
  <Stack direction="row" alignItems="center" spacing={1} width="100%">
    {icon}
    <Typography variant="bodyLarge" sx={{ flexGrow: 1 }}>
      {children}
    </Typography>
    {action}
  </Stack>
);

const Waitlist = ({
  organizationId,
  propertyId,
  applicationId,
  prospectPreferences,
  floorPlans,
  onRefetch,
  applicationStatusName,
}) => {
  const {
    isVisible,
    waitlistSummary,
    hasWaitlistRecord,
    hasV2WaitlistRecord,
    isDeniedOrCanceled,
    isWaitlistActive,
    expanded,
    setExpanded,
    positionRows,
    affordableWaitlistId,
    waitlistV2Programs,
    showAdd,
    isAddDisabled,
    isAddOpen,
    setIsAddOpen,
    handleAddSuccess,
    isDenyOpen,
    setIsDenyOpen,
    isCancelOpen,
    setIsCancelOpen,
    isSelectFromWaitlistOpen,
    setIsSelectFromWaitlistOpen,
    selectFromWaitlistModalState,
    isReinstateOpen,
    setIsReinstateOpen,
    isEditOpen,
    setIsEditOpen,
    showEdit,
    showDenyCancel,
    canReinstate,
    handleActionSuccess,
    handleSelectFromWaitlistSuccess,
    handleReinstateSuccess,
    handleEditSuccess,
    handleOpenSelectFromWaitlist,
  } = useWaitlistCard({
    organizationId,
    propertyId,
    applicationId,
    prospectPreferences,
    floorPlans,
    onRefetch,
    applicationStatusName,
  });

  if (!isVisible) return null;

  const {
    submissionDateTime,
    householdIncome,
    totalAdults,
    totalMinors,
    separateByBedroom,
    incomeLevels,
    preferredBedroomsText,
    preferredFloorPlanName,
    waitlistPreferencesText,
    displacement,
    prefersAccessibleUnit,
    requestingAccommodation,
    accommodationRequested,
  } = waitlistSummary;

  return (
    <Box padding={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="titleLarge" sx={{ flexGrow: 1 }}>
          <FormattedMessage {...messages.cardTitle} />
        </Typography>
        <IconButton
          onClick={() => setExpanded(!expanded)}
          aria-label="toggle-waitlist-summary"
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>

      <Stack spacing={2} paddingTop={2}>
        <SectionTitle
          icon={<LocationAwayIcon sx={{ fontSize: 24 }} />}
          action={
            showEdit ? (
              <Button
                variant="text"
                size="small"
                startIcon={<PencilIcon />}
                onClick={() => setIsEditOpen(true)}
              >
                <Typography variant="labelMedium" color="inherit">
                  <FormattedMessage {...messages.edit} />
                </Typography>
              </Button>
            ) : null
          }
        >
          <FormattedMessage {...messages.householdInformation} />
        </SectionTitle>

        <Stack spacing={0.5}>
          <DetailRow
            label={<FormattedMessage {...messages.submissionDate} />}
            value={formatDate(submissionDateTime, 'M/D/YYYY [at] h:mm A')}
          />
          <DetailRow
            label={<FormattedMessage {...messages.householdIncome} />}
            value={formatMoney(householdIncome)}
          />
          <DetailRow
            label={<FormattedMessage {...messages.numberOfAdults} />}
            value={totalAdults ?? '---'}
          />
          <DetailRow
            label={<FormattedMessage {...messages.numberOfMinors} />}
            value={totalMinors ?? '---'}
          />
        </Stack>
      </Stack>

      <Divider sx={{ marginY: 2 }} />

      <Stack spacing={2}>
        <SectionTitle icon={<ListIcon sx={{ fontSize: 24 }} />}>
          <FormattedMessage {...messages.waitlistPositions} />
        </SectionTitle>
        <Stack spacing={0.5}>
          {positionRows.length === 0 ? (
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Typography variant="bodyMedium">
                <FormattedMessage {...messages.positionOnWaitlist} />
              </Typography>
              <Typography variant="bodyMedium" color="text.secondary">
                <FormattedMessage {...messages.notOnWaitlist} />
              </Typography>
            </Stack>
          ) : (
            positionRows.map((row) => (
              <Stack
                key={row.key}
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
              >
                <Typography variant="bodyMedium" sx={{ flexGrow: 1 }}>
                  {row.label}
                </Typography>
                {!row.showsStatus && (
                  <Button
                    variant="text"
                    size="small"
                    disabled={!isWaitlistActive}
                    onClick={() => handleOpenSelectFromWaitlist(row)}
                  >
                    <Typography variant="labelMedium" color="inherit">
                      <FormattedMessage {...messages.selectFromWaitlist} />
                    </Typography>
                  </Button>
                )}
                {row.showsStatus ? (
                  <Typography variant="labelMedium">
                    <PositionStatus
                      message={row.statusMessage}
                      status={row.status}
                    />
                  </Typography>
                ) : (
                  <Typography variant="bodyMedium">
                    {row.rank ?? '---'}
                  </Typography>
                )}
              </Stack>
            ))
          )}
        </Stack>

        {isDeniedOrCanceled && hasWaitlistRecord ? (
          // Gated on record presence, not `hasV2WaitlistRecord`: reinstate creates positions
          // rather than acting on existing ones, so a position-less legacy V1 row can still use
          // it (the backend's reinstate no-ops its bulk status update on an empty position set
          // and then runs reconcileAndCalculatePreferenceWeight, which defaults to allowing adds).
          canReinstate ? (
            <Button
              sx={{ width: '40%', alignSelf: 'center' }}
              variant="primarySubtle"
              onClick={() => setIsReinstateOpen(true)}
            >
              <FormattedMessage {...messages.reinstate} />
            </Button>
          ) : null
        ) : !hasV2WaitlistRecord ? (
          showAdd ? (
            <Box sx={{ width: '40%', alignSelf: 'center' }}>
              <ConditionalTooltip
                show={isAddDisabled}
                title={<FormattedMessage {...messages.addClosedTooltip} />}
                variant={'light'}
                placement={'top'}
                containerSX={{ width: '100%' }}
              >
                <Button
                  sx={{ width: '100%' }}
                  variant="primarySubtle"
                  disabled={isAddDisabled}
                  onClick={() => setIsAddOpen(true)}
                >
                  <FormattedMessage {...messages.add} />
                </Button>
              </ConditionalTooltip>
            </Box>
          ) : null
        ) : showDenyCancel ? (
          <Stack direction="row" spacing={2}>
            <Button
              sx={{ width: '100%' }}
              variant="primarySubtle"
              onClick={() => setIsDenyOpen(true)}
            >
              <FormattedMessage {...messages.deny} />
            </Button>
            <Button
              sx={{ width: '100%' }}
              variant="primarySubtle"
              onClick={() => setIsCancelOpen(true)}
            >
              <FormattedMessage {...messages.cancel} />
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {expanded && (
        <>
          <Divider sx={{ marginY: 2 }} />
          <Stack spacing={2}>
            <SectionTitle icon={<MoneyAltIcon sx={{ fontSize: 24 }} />}>
              <FormattedMessage {...messages.incomeLevels} />
            </SectionTitle>
            <Stack spacing={1}>
              {incomeLevels.map((row) => (
                <Stack
                  key={row.programName}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="bodyMedium">
                    {row.programName}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="bodySmall">
                      {row.level ?? '---'}
                    </Typography>
                    {row.level && INCOME_LEVEL_DOT_COLOR[row.level] && (
                      <StatusIndicator
                        sx={{
                          backgroundColor: INCOME_LEVEL_DOT_COLOR[row.level],
                        }}
                        radius={4}
                      />
                    )}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Divider sx={{ marginY: 2 }} />

          <Stack spacing={2}>
            <SectionTitle icon={<BedIcon sx={{ fontSize: 24 }} />}>
              <FormattedMessage {...messages.preferences} />
            </SectionTitle>
            <Stack spacing={0.5}>
              <DetailRow
                label={<FormattedMessage {...messages.preferredBedrooms} />}
                value={preferredBedroomsText}
              />
              <DetailRow
                label={<FormattedMessage {...messages.preferredFloorplan} />}
                value={
                  <Typography
                    variant="bodyMedium"
                    color="primary.main"
                    component="span"
                  >
                    {preferredFloorPlanName ?? '---'}
                  </Typography>
                }
              />
              <DetailRow
                label={<FormattedMessage {...messages.waitlistPreferences} />}
                value={waitlistPreferencesText ?? '---'}
              />
            </Stack>
          </Stack>

          <Divider sx={{ marginY: 2 }} />

          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccessibleIcon sx={{ fontSize: 24 }} />
              <Typography variant="bodyLarge">
                <FormattedMessage {...messages.accommodations} />
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <DetailRow
                label={<FormattedMessage {...messages.displacement} />}
                value={displacement ?? '---'}
              />
              <DetailRow
                label={<FormattedMessage {...messages.accessibleUnit} />}
                value={
                  <FormattedMessage
                    {...(prefersAccessibleUnit ? messages.yes : messages.no)}
                  />
                }
              />
              <DetailRow
                label={
                  <FormattedMessage {...messages.accommodationsRequested} />
                }
                value={
                  requestingAccommodation === null ? (
                    '---'
                  ) : (
                    <FormattedMessage
                      {...(requestingAccommodation
                        ? messages.yes
                        : messages.no)}
                    />
                  )
                }
              />
              {requestingAccommodation && accommodationRequested && (
                <Typography variant="bodyMedium">
                  {accommodationRequested}
                </Typography>
              )}
            </Stack>
          </Stack>
        </>
      )}

      <DenyFromWaitlist
        organizationId={organizationId}
        propertyId={propertyId}
        affordableWaitlistId={affordableWaitlistId}
        open={isDenyOpen}
        handleClose={() => setIsDenyOpen(false)}
        onSuccess={() => {
          setIsDenyOpen(false);
          handleActionSuccess();
        }}
      />
      <CancelFromWaitlist
        organizationId={organizationId}
        propertyId={propertyId}
        affordableWaitlistId={affordableWaitlistId}
        open={isCancelOpen}
        handleClose={() => setIsCancelOpen(false)}
        onSuccess={() => {
          setIsCancelOpen(false);
          handleActionSuccess();
        }}
      />
      <SelectFromWaitlist
        organizationId={organizationId}
        propertyId={propertyId}
        open={isSelectFromWaitlistOpen}
        handleClose={() => setIsSelectFromWaitlistOpen(false)}
        modalState={selectFromWaitlistModalState}
        refetchPositions={handleSelectFromWaitlistSuccess}
        separateByBedroom={separateByBedroom}
        waitlistV2Programs={waitlistV2Programs}
      />
      <EditWaitlist
        organizationId={organizationId}
        propertyId={propertyId}
        applicationId={applicationId}
        floorPlans={floorPlans}
        open={isEditOpen}
        handleClose={() => setIsEditOpen(false)}
        onSuccess={handleEditSuccess}
      />
      <ReinstateFromWaitlist
        organizationId={organizationId}
        propertyId={propertyId}
        applicationId={applicationId}
        floorPlans={floorPlans}
        open={isReinstateOpen}
        handleClose={() => setIsReinstateOpen(false)}
        onSuccess={() => {
          setIsReinstateOpen(false);
          handleReinstateSuccess();
        }}
      />
      <AddToWaitlist
        organizationId={organizationId}
        propertyId={propertyId}
        applicationId={applicationId}
        floorPlans={floorPlans}
        open={isAddOpen}
        handleClose={() => setIsAddOpen(false)}
        onSuccess={() => {
          setIsAddOpen(false);
          handleAddSuccess();
        }}
      />
    </Box>
  );
};

export default Waitlist;
